import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {BoxTraverser} from '@mintdata-wsp/common/controllers/surface/BoxVisitor';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {VisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualComponent';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {LogicBridge} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/LogicBridge';
import {Page, PageId, PageName} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {ScrollbarModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scrollbar/ScrollbarModel';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {SurfaceState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/SurfaceState';
import {
    SurfaceBounds,
    SurfacePoint,
    SurfaceSize
} from '@mintdata-wsp/common/state/ui/visualDesigner/SurfaceEditorState';
import {NameUtil} from '@mintdata-wsp/common/util/NameUtil';
import {BoxHostId, BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {PageInfo} from '@mintdata/mint-kit/api/models/PageInfo';
import {Point} from '@mintdata/mint-kit/api/models/Point';
import {Size} from '@mintdata/mint-kit/api/models/Size';
import {Units} from '@mintdata/mint-kit/api/models/Units';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {css} from 'glamor';
import {action, runInAction} from 'mobx';

export class SurfaceAreaController
{
    public getState():SurfaceState | null
    {
        const designState = commonControllers.designState.getDesignState();

        return designState ? designState.surface : null;
    }

    public findPageById(pageId:PageId):Page | null
    {
        const state:SurfaceState = this.getState();

        return state.pages.get(pageId);
    }

    public findPageByName(name:PageName):Page
    {
        const {pages} = this.getState();
        let result = null;

        pages.forEach((page:Page) =>
        {
            if( page.name === name )
                result = page;
        });

        return result;
    }

    public findBoxById(pageId:PageId, boxId:BoxId):Box | null
    {
        const page:Page = this.findPageById(pageId);

        return page ? page.artboard.allBoxes.get(boxId) : null;
    }

    public findBoxByName(pageId:PageId, boxName:string):Box | null
    {
        const page:Page = this.findPageById(pageId);

        return page
            ? Array.from(page.artboard.allBoxes.values()).find((box:Box) => this.getBoxName(box) === boxName)
            : null;
    }

    @action.bound
    public async addNewBox(page:Page,
                           parent:Box = null,
                           parentHostId:BoxHostId = null,
                           component:VisualComponent,
                           name?:string,
                           position?:SurfacePoint,
                           size?:SurfaceSize,
                           runInActionAfterBoxAdded?:(box:Box) => void):Promise<Box>
    {
        const {artifacts, designState} = commonControllers;

        await artifacts.resolveVisualComponent(component);

        return runInAction(() =>
        {
            const box:Box = Box.createVisualBox(component, page, parent, [], parentHostId);

            if( !name )
                name = this._generateNewBoxName(page, box as any);

            if( !position )
                position = SurfacePoint.from(0, 0);

            if( !size )
                size = this.componentInitialSize(component);

            box.componentDesc.layoutLeftProp.setInnerValue(Units.fromPixels(position.x));
            box.componentDesc.layoutTopProp.setInnerValue(Units.fromPixels(position.y));
            box.componentDesc.layoutWidthProp.setInnerValue(Units.fromPixels(size.width));
            box.componentDesc.layoutHeightProp.setInnerValue(Units.fromPixels(size.height));
            box.componentDesc.nameProp.setInnerValue(AString.fromString(name));

            page.artboard.addBox(box);
            designState.addVisualArtifact(component.visualArtifactRef);

            if( runInActionAfterBoxAdded )
                runInActionAfterBoxAdded(box);

            return box;
        });
    }

    @action.bound
    public cloneBox(srcPage:Page,
                    destPage:Page,
                    destParentBox:Box,
                    box:Box,
                    newBoxName:string = null,
                    keepBindings:boolean = true,
                    newBounds:SurfaceBounds = null):Box
    {
        Assert.yes(box.component.ref.isResolved, 'Should only clone resolved boxes!');

        const {bindings, spreadsheets} = commonControllers;
        const {namedRanges} = spreadsheets;

        const state:SurfaceState = this.getState();

        if( !state )
        {
            console.warn(`Surface state was null in SurfaceAreaController.addNewVisualComponent(); taking no action`);
            return;
        }

        const curLayer = destPage.artboard;

        const newBox:Box = box.clone(destPage, destParentBox);

        if( newBounds )
        {
            newBox.componentDesc.layoutLeftProp.setInnerValue(Units.fromPixels(newBounds.position.x));
            newBox.componentDesc.layoutTopProp.setInnerValue(Units.fromPixels(newBounds.position.y));
            newBox.componentDesc.layoutWidthProp.setInnerValue(Units.fromPixels(newBounds.size.width));
            newBox.componentDesc.layoutHeightProp.setInnerValue(Units.fromPixels(newBounds.size.height));
        }

        if( !newBoxName )
            newBoxName = this._generateNewBoxName(destPage, box, true);

        curLayer.addBox(newBox);

        newBox.componentDesc.nameProp.setInnerValue(AString.fromString(newBoxName));

        newBox.componentDesc.allPropsList.forEach(prop =>
        {
            const bindingIds = [...prop.bindingIds.filter(bindingId => !!bindingId)];
            prop.clearBindings();

            // don't copy bindings
            if( !keepBindings )
                return;

            // do copy bindings
            for( const bindingId of bindingIds )
            {
                const binding = bindings.getBinding(bindingId);
                // Assert.yes(binding, `Unknown binding for bindingId: ${bindingId}`);

                if( binding )
                {
                    const newRange = binding.range.name ? binding.range : namedRanges.copyNamedRange(binding.range);
                    const newBinding = binding.copyForClonedBox(newBox, prop, newRange);

                    bindings.bindProperty(prop, newBinding);
                }
                else
                    console.error(
                        `Unknown binding for bindingId: ${bindingId}, component: ${box.componentDesc.name.toString()}`
                    );
            }
        });

        newBox.addChildren(
            box.children.map(
                (childBox:Box) => this.cloneBox(srcPage, destPage, newBox, childBox, null)
            )
        );

        return newBox;
    }

    public removeVisualComponent(box:Box):void
    {
        const surfaceLayer = box.page.artboard;

        Assert.yes(box.componentDesc);
        Assert.yes(box.page);

        const visitor = {
            onBox: function(box:Box)
            {
                const {bindings} = commonControllers;

                box.componentDesc.propsList.forEach(prop =>
                    [...prop.bindingIds].forEach(bindingId => bindings.removeBindingById(prop, bindingId))
                );

                box.componentDesc.eventPropsMapByName.forEach((eventProp) =>
                    bindings.removeBindingById(eventProp, eventProp.bindingId)
                );

                box.componentDesc.baseEventPropsMapByName.forEach((eventProp) =>
                    bindings.removeBindingById(eventProp, eventProp.bindingId)
                );
            }
        }

        BoxTraverser.visit(box, visitor, false, true);

        // todo: traverse all box children and remove all their's bindings

        if( box.parent )
        {
            box.parent.removeChild(box);
            surfaceLayer.removeBox(box);
        }
        else
        {
            const boxes = surfaceLayer.boxes;

            const boxIndex = boxes.findIndex((b:Box) => (b.id === box.id));
            Assert.yes(boxIndex >= 0);

            surfaceLayer.removeRootBox(boxIndex);
        }
    }

    public getAllBoxes(page:Page, reverse:boolean = false):Box[]
    {
        const boxes:Box[] = page ? page.artboard.boxes : [];

        return this.flattenBoxesDeep(boxes, reverse);
    }

    public flattenBoxesDeep(boxes:Box[], reverse:boolean = false):Box[]
    {
        const result = [];

        for( const box of reverse ? boxes.reverse() : boxes )
        {
            result.push(box);

            if( box.children.length )
                result.push(...this.flattenBoxesDeep(box.children, reverse));
        }

        return result;
    }

    public getBoxName(box:Box):string
    {
        if( box.isViewport )
            return box.page.name;

        const defaultName = NameUtil.getDisplayName(box.component);
        let boxName = null;

        if( this.areBoxesResolved([box]) )
        {
            const {name} = box.componentDesc;

            boxName = name && name.toString();
        }

        return boxName || defaultName;
    };

    public areBoxesResolved(boxes:Box[]):boolean
    {
        return boxes.every((box) => box.component.ref.isResolved);
    }

    public componentInitialSize(component:VisualComponent):SurfaceSize
    {
        if( component.ref.isResolved )
        {
            const {componentConfig} = component.ref.resolvedComponent;

            if( componentConfig.width && componentConfig.height )
                return new SurfaceSize(componentConfig.width, componentConfig.height);
            else
            {
                console.warn(`Warning: ${component.name} component didn't specify initial size; using default of 250,250`);
                return new SurfaceSize(250, 250);
            }
        }
        else
            return new SurfaceSize(250, 250);
    }

    @action.bound
    public onBaseEvent(eventProp:EventProp, repeater:Repeater, event:Object, componentId?:string):void
    {
        const {bindings} = commonControllers;

        if( eventProp.isEnabled )
        {
            const eventBinding = bindings.getEventBinding(eventProp.bindingId);
            if( !eventBinding )
                return;

            const eventFrame = LogicBridge.getEventFrame(
                eventProp.name,
                event,
                eventProp.box ? eventProp.box.componentDesc.name.toString() : 'Application',
                eventProp.box ? eventProp.box.componentDesc.componentConfig.name : 'Application',
                componentId ? componentId : eventProp.box ? eventProp.box.id : '',
                repeater
            );

            bindings.reEvaluateLazySlot(
                eventBinding,
                repeater,
                eventFrame
            );
        }
    }

    public getPageInfo():PageInfo
    {
        const {area} = rootState.ui.visualDesigner.surfaceEditor;
        const {zoom} = area;

        const panPosition:Point = area.panModeState.position;
        let areaPosition:Point = Point.zero();

        if( area.surfaceAreaDomElement )
        {
            const areaRect:ClientRect = area.surfaceAreaDomElement.getBoundingClientRect();
            areaPosition = Point.from(areaRect.left, areaRect.top);
        }

        const pageOffset:Point = Point.from(
            areaPosition.x + panPosition.x * zoom,
            areaPosition.y + panPosition.y * zoom
        );

        return new PageInfo(pageOffset);
    }

    protected _generateNewBoxName(page:Page, box:Box, isCloning:boolean = false):string
    {
        const allBoxes:Box[] = this.getAllBoxes(page);
        const existingNames:string[] = allBoxes.map((box) => this.getBoxName(box).toLowerCase());

        const boxName = this.getBoxName(box);
        if( existingNames.indexOf(boxName.toLowerCase()) === -1 )
            return boxName;

        let initialName = boxName;

        if( isCloning )
        {
            const regex1 = '^.+ Copy';
            const regex2 = '( ([2-9]|[1-9]\\d+))?$';

            const isCloneAlready = boxName.match(new RegExp(`${regex1}${regex2}`, 'i'));

            if( isCloneAlready )
                initialName = boxName.replace(new RegExp(regex2, 'i'), '');
            else
                initialName = `${boxName} Copy`;
        }

        let newBoxName = initialName;
        let index = 2;

        while( true )
        {
            if( existingNames.indexOf(newBoxName.toLowerCase()) === -1 )
                break;

            newBoxName = `${initialName} ${index}`;
            index += 1;
        }

        return newBoxName;
    }

    public getScrollbarStyles():any
    {
        const surfaceState:SurfaceState = this.getState();

        if( !surfaceState ) return null;

        const scrollbarProp:Prop<ScrollbarModel> = surfaceState.propsByName.get(SystemPropsConfigs.SCROLLBAR_PROP_NAME);
        const {value: scrollbarModel} = scrollbarProp;

        return !scrollbarModel.isEnabled.value ? null :
            css({
                'body & ::-webkit-scrollbar':
                    {
                        width: scrollbarModel.width.value,
                        height: scrollbarModel.width.value
                    },

                'body & ::-webkit-scrollbar-track':
                    {
                        ...scrollbarModel.trackBackground.toCss(),
                        ...scrollbarModel.trackBorder.toCSS(),
                        boxShadow: scrollbarModel.trackShadow.toCssString() + ' inset'
                    },

                'body & ::-webkit-scrollbar-thumb':
                    {
                        backgroundClip: 'padding-box',
                        ...scrollbarModel.thumbBackground.toCss(),
                        ...scrollbarModel.thumbBorder.toCSS(),
                        boxShadow: scrollbarModel.thumbShadow.toCssString() + ' inset'
                    }
            });
    }

    @action
    public setViewportSize(viewportSize:Size):void
    {
        const surfaceState:SurfaceState = this.getState();

        if( !surfaceState ) return;

        surfaceState.viewportSize = viewportSize;
	}

    public getComponentsWithSelfCursor():string[]
    {
        return [
            // *** General
            // 'box',
            'text',
            // 'label',
            // 'paragraph',
            // 'button',
            // 'image',
            // 'icon',
            // 'progressbar',
            'tooltipdecorator',
            'popovercontainer',

            // *** Widgets
            'calendar', 'table', 'chart', 'map', 'soundplayer', 'sqleditor', 'jsoneditor', 'videoplayer',

            // *** Forms
            // 'textinput', 'dropdown', 'textarea', 'checkbox', 'switch', 'radiobuttongroup', 'timeinput',

            // *** AdvancedForms
            // 'numericinput', 'dateinput', 'daterangeinput',
            // 'slider', 'rangeslider',
            // 'buttongroup', 'togglebuttonset',

            'richtexteditor', 'filepicker',

            // *** Navigation
            'navigation', 'tabs', 'splitview',
        ];
    }

    public isComponentWithSelfCursor(componentName:string):boolean
    {
        return this.getComponentsWithSelfCursor().indexOf(componentName.toLowerCase()) !== -1;
    }
}
